"use client";
import { Track } from "@/src/lib/tracking";

import RetryTranslations from "@translate/screens/plan/constants/planDashboardTokens";
import Translations from "@translate/components/OnboardingWizard/Paywall/useOnboardingPaywallPurchase";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import {
  MemberProductLevelEnum,
  MemberStatusEnum,
  PayChannel,
} from "@features/api/memberApi";
import {
  getPaymentCheckErrorMessage,
  useAppPurchaseFlow,
} from "@hooks/useAppPurchaseFlow";
import { useMemberFeature } from "@hooks/useMemberFeature";
import { useMemberUser } from "@hooks/useMemberUser";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import {
  ProductPayCycle,
  ProductStatus,
  ProductType,
} from "@components/Member/CurrentPlan/constants";
import { I18nUtil } from "@utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@config/constant";
import {
  trackObPaywallPurchaseSuccess,
  trackObPaywallTrialStart,
  trackOnboardingAnalytics,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { navigateToMemberPricingPage } from "@utils/member";
import { EdgenModalCreateAble } from "@components/EdgenModal";
import { opportunitiesQueryKeys } from "@/src/hooks/useOpportunities";
import type {
  UseOnboardingPaywallPurchaseParams,
  UseOnboardingPaywallPurchaseResult,
} from "./types";

import { usePayment } from "@hooks/usePayment";
import { getAvailableChannels } from "@hooks/memberPlansData";
import { isOnboardingTrialReady } from "@components/Member/Subscription/onboardingPricingOffers";
import { rootStore } from "@store/rootStore";
import { getOnboardingV3Session } from "@hooks/onboarding/onboardingV3SessionStorage";
import {
  ONBOARDING_CHECKOUT_QUERY,
  pollOnboardingMembership,
  readOnboardingCheckout,
  saveOnboardingCheckout,
} from "@hooks/onboarding/onboardingCheckoutContinuation";
import { PLAN_DIAGNOSIS_SOURCE } from "@hooks/plan/planDiagnosisSource";
import { sendBeginCheckoutGtm } from "@utils/member/purchaseGtm";
import { channelRequiresReturnConfirm } from "@utils/member/checkoutChannel";
import { confirmCheckoutOnce } from "@hooks/useCheckoutReturnConfirm";
import { ROUTER_PATH } from "@config/router";

const I18n = I18nUtil.createScoped(Translations);
const RetryI18n = I18nUtil.createScoped(RetryTranslations);

/** 待生效付费墙自动轮询会员态的间隔：5 次覆盖约 24 秒（pollOnboardingMembership 固定 5 次） */
const PENDING_POLL_INTERVAL_MS = 6000;

const waitPendingPoll = () =>
  new Promise<void>((resolve) => setTimeout(resolve, PENDING_POLL_INTERVAL_MS));

export const useOnboardingPaywallPurchase = ({
  onClose,
  firstWall,
  selectedProduct,
  selectedPlanId,
  checkoutPending,
  trackingModuleName,
  analyticsSource,
  onWebCheckoutRedirect,
}: UseOnboardingPaywallPurchaseParams): UseOnboardingPaywallPurchaseResult => {
  const queryClient = useQueryClient();
  const { handlePaymentCheck } = usePayment();
  const purchaseLock = useRef(false);
  const [pendingAttempt] = useState(() => {
    if (!firstWall) {
      return undefined;
    }
    const pending = readOnboardingCheckout(rootStore.user.ownerInfo?.id ?? "");
    return pending?.phase === "pending" ? pending : undefined;
  });
  const isCheckoutPending = !!checkoutPending || !!pendingAttempt;
  const pollRun = useRef<{ cancelled: boolean } | undefined>(undefined);
  const confirmed = useRef(false);
  const lifecycle = useRef({ active: false, generation: 0 });
  useEffect(() => {
    lifecycle.current.active = true;
    lifecycle.current.generation += 1;
    return () => {
      lifecycle.current.active = false;
    };
  }, []);
  const { data: memberProductData, trialList } = useMemberFeature();
  const {
    data: memberUser,
    isPending: membershipPending,
    isLoading: membershipLoading,
    isError: membershipError,
    refetch: refetchMemberUser,
  } = useMemberUser();
  const { purchaseInApp, shouldTryNativePayment } = useAppPurchaseFlow();
  const isReady =
    !firstWall ||
    shouldTryNativePayment ||
    (!!memberUser &&
      !membershipPending &&
      !membershipLoading &&
      !membershipError);
  const router = useShellRouter();
  const [isLoading, setIsLoading] = useState(false);

  const completeYearDisplayProduct = useMemo(() => {
    const yearProducts = (memberProductData || []).filter(
      (item) =>
        item.pay_cycle === ProductPayCycle.YEAR &&
        item.status === ProductStatus.ACTIVE &&
        item.type === ProductType.NORMAL &&
        item.level === MemberProductLevelEnum.EXPERT,
    );
    return yearProducts.length > 0 ? yearProducts[0] : undefined;
  }, [memberProductData]);

  const completeYearProduct = useMemo(() => {
    const trialProduct = trialList?.find(
      (item) => item.renewal_id === completeYearDisplayProduct?.renewal_id,
    );
    if (trialProduct) {
      return trialProduct;
    }
    return completeYearDisplayProduct;
  }, [completeYearDisplayProduct, trialList]);

  const annualDisplayPrice = Number(
    completeYearDisplayProduct?.price ?? completeYearProduct?.price ?? 0,
  );

  const isBuyTrialNow = completeYearProduct?.type === ProductType.TRIAL;

  const buttonText = useMemo(() => {
    if (isBuyTrialNow) {
      if (completeYearProduct?.price && Number(completeYearProduct.price) > 0) {
        return I18n.t("Try 7 Days for Just $%{var0}", {
          var0: Number(completeYearProduct.price).toFixed(2),
        });
      }
      return I18n.t("Start Your 7-Day Free Trial");
    }
    return I18n.t("Subscribe now");
  }, [completeYearProduct?.price, isBuyTrialNow]);

  const onUnlockSuccess = useCallback(async () => {
    const source = analyticsSource ?? "onboarding";
    if (!firstWall) {
      trackObPaywallPurchaseSuccess(source, "");
    }
    Track.log("info", "OnboardingPaywall", "onUnlockSuccess start", {
      shouldTryNativePayment,
    });
    if (shouldTryNativePayment) {
      const maxAttempts = 5;
      const intervalMs = 1000;
      let isActive = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await refetchMemberUser();
        const status = result?.data?.status;
        Track.log("info", "OnboardingPaywall", "member poll", {
          attempt: attempt + 1,
          maxAttempts,
          status,
        });
        if (
          status === MemberStatusEnum.ACTIVE &&
          (!firstWall ||
            (result.data?.product?.level &&
              result.data.product.level !== "FREE"))
        ) {
          isActive = true;
          break;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
      if (!isActive) {
        Track.log(
          "warn",
          "OnboardingPaywall",
          "member not ACTIVE after polls, fallback pricing",
          { maxAttempts, intervalMs },
        );
        onWebCheckoutRedirect?.();
        onClose?.();
        navigateToMemberPricingPage(router, { tab: "pricing" });
        return;
      }
    }
    Track.log("info", "OnboardingPaywall", "onUnlockSuccess complete");
    EdgenModalCreateAble.end(true);
    await queryClient.invalidateQueries({
      queryKey: [...opportunitiesQueryKeys.all, "list"],
    });
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
  }, [
    firstWall,
    analyticsSource,
    onClose,
    queryClient,
    refetchMemberUser,
    router,
    shouldTryNativePayment,
  ]);

  /**
   * 待生效付费墙查会员态：挂载时自动查；用户点「Check payment status」时 `confirmFirst`，
   * 需主动确认的渠道（AIRWALLEX）先用暂存 session_id 补确认一次再查（Q6：原来只轮询会员态，
   * 后端补好订阅前用户点多少次都不会触发开通）。
   */
  const checkPendingMembership = useCallback(
    async (confirmFirst = false) => {
      if (purchaseLock.current || confirmed.current) {
        return;
      }
      const ownerId = rootStore.user.ownerInfo?.id ?? "";
      const generation = lifecycle.current.generation;
      const run = { cancelled: false };
      pollRun.current = run;
      const isCurrent = () =>
        !run.cancelled &&
        lifecycle.current.active &&
        lifecycle.current.generation === generation &&
        rootStore.user.ownerInfo?.id === ownerId &&
        (!pendingAttempt ||
          (pendingAttempt.ownerId === ownerId &&
            readOnboardingCheckout(ownerId)?.id === pendingAttempt.id));
      if (!isCurrent()) {
        return;
      }
      purchaseLock.current = true;
      setIsLoading(true);
      try {
        if (
          confirmFirst &&
          pendingAttempt &&
          channelRequiresReturnConfirm(pendingAttempt.channel)
        ) {
          await confirmCheckoutOnce({
            planId: pendingAttempt.product.id ?? "",
            channel: pendingAttempt.channel,
          });
          if (!isCurrent()) {
            return;
          }
        }
        const active = await pollOnboardingMembership(
          refetchMemberUser,
          waitPendingPoll,
          isCurrent,
        );
        if (!isCurrent()) {
          return;
        }
        if (active && pendingAttempt) {
          confirmed.current = true;
          EdgenModalCreateAble.end(true);
        } else {
          OspToast.info({ content: RetryI18n.t("Please try again later.") });
        }
      } finally {
        if (pollRun.current === run) {
          purchaseLock.current = false;
          if (lifecycle.current.active) {
            setIsLoading(false);
          }
        }
      }
    },
    [pendingAttempt, refetchMemberUser],
  );

  useEffect(() => {
    if (!firstWall || !isCheckoutPending) {
      return;
    }
    void checkPendingMembership();
    return () => {
      if (pollRun.current) {
        pollRun.current.cancelled = true;
      }
      purchaseLock.current = false;
    };
  }, [firstWall, isCheckoutPending, checkPendingMembership]);

  const handleFirstWallPurchase = useCallback(
    // channel 无默认（EDG-552）：web 由调用方传 pickCheckoutChannels 的结果；App 内走 IAP 不使用
    async (channel?: PayChannel) => {
      if (purchaseLock.current) {
        return;
      }
      const ownerId = rootStore.user.ownerInfo?.id ?? "";
      const generation = lifecycle.current.generation;
      const isCurrent = () =>
        lifecycle.current.active &&
        lifecycle.current.generation === generation &&
        rootStore.user.ownerInfo?.id === ownerId;
      const pending = readOnboardingCheckout(ownerId);
      if (isCheckoutPending || pending?.phase === "pending") {
        await checkPendingMembership(true);
        return;
      }
      if (!selectedProduct?.id || !selectedPlanId || !ownerId) {
        return;
      }
      purchaseLock.current = true;
      setIsLoading(true);
      try {
        if (shouldTryNativePayment) {
          const result = await purchaseInApp(selectedProduct, {
            annualOffersOnly: true,
            onSuccess: onUnlockSuccess,
          });
          if (!result.success) {
            if (result.iapUnavailable) {
              onWebCheckoutRedirect?.();
              onClose?.();
              navigateToMemberPricingPage(router, { tab: "pricing" });
            } else if (result.error) {
              OspToast.error({ content: result.error });
            }
          }
          return;
        }
        // 放行产品当前任意 ACTIVE web 渠道（原只认 STRIPE / PAYPAL，AIRWALLEX 会被静默丢弃）
        if (
          !channel ||
          !getAvailableChannels(selectedProduct).includes(channel)
        ) {
          return;
        }
        if (!isReady || !isOnboardingTrialReady(selectedProduct, channel)) {
          OspToast.error({ content: getPaymentCheckErrorMessage({}) });
          return;
        }
        const preview = getOnboardingV3Session()?.planPreview;
        const draft =
          pending?.draft ??
          (preview?.draftId
            ? {
                draftGoalId: preview.draftId,
                draftTitle: preview.title,
                summary: preview.summary ?? "",
                source: PLAN_DIAGNOSIS_SOURCE.ONBOARDING_V3_0_1,
              }
            : undefined);
        if (!draft) {
          OspToast.error({ content: getPaymentCheckErrorMessage({}) });
          return;
        }
        const id = crypto.randomUUID();
        const { data: checkoutUrl, error } = await handlePaymentCheck(
          selectedProduct.id,
          {
            returnPath: ROUTER_PATH.MEMBER,
            payChannel: channel,
            returnQuery: { [ONBOARDING_CHECKOUT_QUERY]: id },
            product: selectedProduct,
          },
        );
        if (!isCurrent()) {
          return;
        }
        if (error || !checkoutUrl) {
          OspToast.error({ content: getPaymentCheckErrorMessage(error ?? {}) });
          return;
        }
        if (
          !saveOnboardingCheckout({
            id,
            ownerId,
            createdAt: Date.now(),
            selectedPlanId,
            returnPath: `${window.location.pathname}${window.location.search}`,
            product: {
              id: selectedProduct.id,
              name: selectedProduct.name,
              price: selectedProduct.price,
            },
            channel,
            analyticsSource: analyticsSource ?? "onboarding",
            draft,
            phase: "checkout",
          })
        ) {
          OspToast.error({ content: getPaymentCheckErrorMessage({}) });
          return;
        }
        sendBeginCheckoutGtm(selectedProduct, { payChannel: channel });
        onWebCheckoutRedirect?.();
        onClose?.();
        window.location.assign(checkoutUrl);
      } catch {
        if (shouldTryNativePayment || isCurrent()) {
          OspToast.error({ content: getPaymentCheckErrorMessage({}) });
        }
      } finally {
        purchaseLock.current = false;
        setIsLoading(false);
      }
    },
    [
      selectedProduct,
      selectedPlanId,
      isReady,
      isCheckoutPending,
      checkPendingMembership,
      shouldTryNativePayment,
      purchaseInApp,
      onUnlockSuccess,
      router,
      handlePaymentCheck,
      analyticsSource,
      onWebCheckoutRedirect,
      onClose,
      refetchMemberUser,
    ],
  );

  const handlePurchase = useCallback(
    (channel?: PayChannel) => {
      if (firstWall) {
        void handleFirstWallPurchase(channel);
        return;
      }

      trackObPaywallTrialStart("");
      trackOnboardingAnalytics("ob_paywall_trial_click", {
        subscription_id: "",
      });
      Track.event("spend_membership", {
        module_name: trackingModuleName ?? "paywall_onboarding",
        trigger_symbol: "",
        current_balance: undefined,
      });

      if (shouldTryNativePayment) {
        if (!completeYearProduct) {
          onWebCheckoutRedirect?.();
          onClose?.();
          navigateToMemberPricingPage(router, { tab: "pricing" });
          return;
        }
        setIsLoading(true);
        void purchaseInApp(completeYearProduct, {
          onSuccess: () => {
            void onUnlockSuccess().finally(() => {
              setIsLoading(false);
            });
          },
        }).then((result) => {
          if (!result.success) {
            setIsLoading(false);
            if (result.iapUnavailable) {
              onWebCheckoutRedirect?.();
              onClose?.();
              navigateToMemberPricingPage(router, { tab: "pricing" });
            } else {
              OspToast.error({
                content: result.error ?? I18n.t("Purchase failed."),
              });
            }
          }
        });
        return;
      }

      onWebCheckoutRedirect?.();
      onClose?.();
      navigateToMemberPricingPage(router, { tab: "pricing" });
    },
    [
      firstWall,
      handleFirstWallPurchase,
      completeYearProduct,
      onWebCheckoutRedirect,
      onClose,
      onUnlockSuccess,
      purchaseInApp,
      router,
      shouldTryNativePayment,
      trackingModuleName,
    ],
  );

  return {
    isCheckoutPending,
    shouldTryNativePayment,
    isReady,
    annualDisplayPrice,
    buttonText,
    isBuyTrialNow,
    isLoading,
    handlePurchase,
  };
};
